/* eslint-disable import/no-duplicates -- date-fns locale is a separate module */
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { format, isBefore, parseISO, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSetAtom } from "jotai";
import { useState, type JSX } from "react";

import { graphql } from "@repo/graphql-types";
import IllustrationCalendar from "@repo/icons/colored/IllustrationCalendar";
import Building1 from "@repo/icons/default/Building1";
import Calendar from "@repo/icons/default/Calendar";
import Clock from "@repo/icons/default/Clock";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import LocationMarker from "@repo/icons/default/LocationMarker";
import { CpsSpinner } from "corpus";

import { Button } from "@/components/ui/button/button";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import {
  EmptyStateV2Description,
  EmptyStateV2Root,
  EmptyStateV2Title,
} from "@/components/ui/empty-state/empty-state-v2";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import {
  fetchQuery,
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
} from "@/hooks/use-graphql";
import { welcomeMeetingAtom } from "@/lib/atoms/welcome-meeting-atom";
import { capitalizeMonth, removeSecondsFromTimeString } from "@/lib/helpers/date";

const WelcomeMeetingDetailsQuery = graphql(`
  query WelcomeMeetingDetailsQuery($codUsuario: Int!) {
    AnimusGetOnboardingVisitDetails(arg1: { userId: $codUsuario }) {
      groupVisitDate
      startTime
      unitName
      unitAddress
    }
    onboarding_visit_metadata(
      where: { groupVisitParticipant: { canceled: { _eq: false } } }
      limit: 1
    ) {
      id
    }
  }
`);

const CancelOnboardingDetailsMutation = graphql(`
  mutation CancelOnboardingDetailsMutation(
    $userId: Int!
    $visitType: String!
    $sellerEmail: String
  ) {
    AnimusCancelSalesVisit(
      arg1: { userId: $userId, visitType: $visitType, sellerEmail: $sellerEmail }
    ) {
      message
    }
  }
`);

const WelcomeMeetingDetailsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { user } = useRouteContext({ from: "__root__" });
  const [showCancelDrawer, setShowCancelDrawer] = useState(false);
  const setWelcomeMeetingForm = useSetAtom(welcomeMeetingAtom);

  const { data, isLoading } = useGraphQL(WelcomeMeetingDetailsQuery, {
    codUsuario: user.codUsuario,
  });

  const { mutate: cancelMutate, isPending: isCancelPending } =
    useGraphQLMutationWithErrorHandler(CancelOnboardingDetailsMutation);

  const handleReschedule = (): void => {
    setWelcomeMeetingForm((prev) => ({
      ...prev,
      isRescheduling: true,
      selectedLocationCode: 0,
      selectedLocationName: "",
      selectedLocationAddress: "",
      date: "",
      timeSlot: null,
    }));
    void navigate({ to: "/onboarding/welcome-meeting/location" });
  };

  const handleConfirmCancel = (): void => {
    cancelMutate(
      {
        userId: user.codUsuario,
        visitType: "onboarding",
        sellerEmail: user.email,
      },
      {
        onSuccess: () => {
          setShowCancelDrawer(false);
          void navigate({ to: "/onboarding/welcome-meeting/skip-reason" });
        },
        onError: () => {
          setShowCancelDrawer(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <RoundedLayout>
        <CpsSpinner />
      </RoundedLayout>
    );
  }

  const visitDetails = data?.AnimusGetOnboardingVisitDetails;
  const visitDate = visitDetails?.groupVisitDate as string | null;
  const parsedDate = visitDate ? parseISO(visitDate) : null;
  const formattedDate = parsedDate
    ? capitalizeMonth(format(parsedDate, "EEEE, dd 'de' MMMM", { locale: ptBR }))
    : null;
  const unitName = visitDetails?.unitName as string | null;
  const unitAddress = visitDetails?.unitAddress as string | null;
  const startTime = visitDetails?.startTime as string | null;
  const formattedTime = startTime ? removeSecondsFromTimeString(startTime) : null;
  const isConsulting = (data?.onboarding_visit_metadata.length ?? 0) > 0;

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton
            onBack={() => {
              void navigate({ to: "/home" });
            }}
          />
        </FlowHeader.Actions>
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <EmptyStateV2Root className="gap-4 py-4">
          <IllustrationCalendar size={80} aria-hidden="true" />

          <div>
            <EmptyStateV2Title className="text-2xl font-medium">
              {isConsulting ? "Reunião de consultoria" : "Reunião de boas vindas"}
            </EmptyStateV2Title>
            <EmptyStateV2Description className="text-md">
              Confira os detalhes da sua reunião agendada.
            </EmptyStateV2Description>
          </div>

          <div className="flex flex-col gap-3 rounded-lg bg-neutral-50 p-4">
            {unitName ? (
              <div className="flex items-center gap-2 text-sm text-neutral-700">
                <Building1
                  size={24}
                  className="fill-primary-400 shrink-0"
                  aria-hidden="true"
                />
                <span>{unitName}</span>
              </div>
            ) : null}
            {unitAddress ? (
              <div className="flex items-start gap-2 text-sm text-neutral-700">
                <LocationMarker
                  size={24}
                  className="fill-primary-400 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span>{unitAddress}</span>
              </div>
            ) : null}
            <div className="flex items-center gap-2 text-sm text-neutral-700">
              <Calendar
                size={24}
                className="fill-primary-400 shrink-0"
                aria-hidden="true"
              />
              <span className="first-letter:uppercase">{formattedDate ?? ""}</span>
            </div>
            {formattedTime ? (
              <div className="flex items-center gap-2 text-sm text-neutral-700">
                <Clock
                  size={24}
                  className="fill-primary-400 shrink-0"
                  aria-hidden="true"
                />
                <span>{formattedTime}</span>
              </div>
            ) : null}
          </div>
        </EmptyStateV2Root>

        <div className="sticky bottom-0 flex flex-col gap-3 pt-4 pb-4 bg-white">
          <Button fullWidth color="secondary" onClick={handleReschedule}>
            Reagendar reunião
          </Button>
          <Button
            fullWidth
            color="secondaryLight"
            onClick={() => setShowCancelDrawer(true)}
            disabled={isCancelPending}
          >
            Pular reunião
          </Button>
        </div>

        <MessageDrawerRoot
          icon={ExclamationCircle}
          variant="secondary"
          open={showCancelDrawer}
          setOpen={setShowCancelDrawer}
        >
          <MessageDrawerTitle>
            {isConsulting
              ? "Tem certeza que deseja pular a reunião de consultoria?"
              : "Tem certeza que deseja pular a reunião de boas vindas?"}
          </MessageDrawerTitle>
          <MessageDrawerBody>
            Este encontro foi pensado para te apoiar nos primeiros passos, garantir uma
            ótima experiência desde o início e te orientar na realização dos seus
            primeiros atendimentos.
          </MessageDrawerBody>
          <MessageDrawerActions>
            <MessageDrawerActionButton
              onClick={handleConfirmCancel}
              loading={isCancelPending}
            >
              Sim, quero pular a reunião
            </MessageDrawerActionButton>
            <MessageDrawerActionButton
              secondary
              onClick={() => setShowCancelDrawer(false)}
            >
              Cancelar
            </MessageDrawerActionButton>
          </MessageDrawerActions>
        </MessageDrawerRoot>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/welcome-meeting/details")({
  component: WelcomeMeetingDetailsPage,
  beforeLoad: async ({ context }) => {
    const data = await fetchQuery(context, WelcomeMeetingDetailsQuery, {
      codUsuario: context.user.codUsuario,
    });

    const visitDetails = data.AnimusGetOnboardingVisitDetails;
    const visitDate = visitDetails?.groupVisitDate as string | undefined;

    if (!visitDate || isBefore(parseISO(visitDate), startOfDay(new Date()))) {
      redirect({ to: "/home", throw: true });
    }
  },
});
